import { Button } from '@zep-us/zus';
import { ChevronRightIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { QuizListItemWithActions } from '../../quiz-list-item/components/QuizListItemWithActions';
import { useOwnerQuizzes } from '../hooks/useOwnerQuizzes';
interface OwnerQuizzesSectionProps {
  /** 퀴즈 소유자 ID */
  ownerId: string;
  /** 퀴즈 소유자 이름 */
  authorName: string;
  /** 현재 퀴즈 ID (결과에서 제외하기 위해) */
  currentQuizId: string;
}

/**
 * OwnerQuizzesSection - 소유자의 다른 퀴즈 섹션
 *
 * @description
 * 현재 퀴즈 소유자가 만든 다른 퀴즈 목록을 세로로 표시합니다.
 * 새로운 atomic 컴포넌트를 사용하여 구성됩니다.
 * 로딩, 에러, 빈 상태를 처리합니다.
 *
 * @example
 * ```tsx
 * <OwnerQuizzesSection ownerId="user-123" currentQuizId="quiz-456" />
 * ```
 */
export const OwnerQuizzesSection = ({
  ownerId,
  authorName,
  currentQuizId
}: OwnerQuizzesSectionProps) => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const {
    data,
    isLoading,
    isError
  } = useOwnerQuizzes(ownerId);

  // 현재 퀴즈를 제외한 목록
  const quizzes = data?.data?.filter(quiz => quiz.id !== currentQuizId) || [];

  // 퀴즈 클릭 핸들러
  const handleQuizClick = (quizId: string) => {
    logger.track('enter_detail_user', {
      location: 'owner-quizzes',
      id: quizId
    });
    void router.push(`/quiz/${quizId}`);
  };

  // 전체 보기 핸들러
  const handleViewAll = () => {
    logger.track('view_all_click', {
      location: 'owner-quizzes',
      ownerId: ownerId
    });
    void router.push(`/profile/${ownerId}`);
  };

  // API 에러 발생 시 섹션 숨김 (유저 삭제 등)
  if (isError) {
    console.warn('[OwnerQuizzesSection] API 에러 발생:', ownerId);
    return null;
  }
  if (isLoading) return null;

  // 퀴즈가 3개 미만이면 섹션 숨김
  if (quizzes.length < 3) {
    return null;
  }
  return <section className="flex w-full flex-col gap-md" data-sentry-component="OwnerQuizzesSection" data-sentry-source-file="OwnerQuizzesSection.tsx">
      {/* 섹션 헤더 - 제목 + 전체 보기 링크 */}
      <div className="flex h-[44px] items-center justify-between gap-[10px]">
        <h2 className="text-heading-lg-extrabold text-default">
          {t('detail.ownerQuizzes.title', {
          authorName
        })}
        </h2>
        <Button size="sm" variant="transparent" intent="neutral" onClick={handleViewAll} rightSlot={<ChevronRightIcon size="xs" />} data-sentry-element="Button" data-sentry-source-file="OwnerQuizzesSection.tsx">
          {t('detail.ownerQuizzes.viewAll')}
        </Button>
      </div>

      {/* 성공 상태 - 퀴즈 목록 */}

      <div className="flex flex-col">
        {quizzes.map((quiz, index) => <QuizListItemWithActions key={quiz.id} quiz={quiz} isLastItem={index === quizzes.length - 1} onQuizClick={handleQuizClick} />)}
      </div>
    </section>;
};